import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { MessageSquare, X } from 'lucide-react';
import { NAV_GROUPS } from './Sidebar';
import { useAuth } from '@/context/AuthContext';

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSelect = useCallback((path) => {
    onClose();
    if (path === '__chanakya__') {
      window.dispatchEvent(new CustomEvent('bm:open-chanakya'));
      return;
    }
    navigate(path);
  }, [navigate, onClose]);

  if (!open) return null;

  const visibleGroups = user
    ? NAV_GROUPS
    : NAV_GROUPS.filter(g => g.public);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />

      <div
        className="relative w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        <Command
          className="bg-white dark:bg-stone-900 rounded-2xl shadow-2xl border border-stone-200 dark:border-stone-700 overflow-hidden"
          label="Command palette"
        >
          <div className="flex items-center border-b border-stone-200 dark:border-stone-700 px-4">
            <Command.Input
              placeholder="Search pages or ask Chanakya…"
              autoFocus
              className="flex-1 h-12 text-sm text-stone-900 dark:text-stone-100 bg-transparent outline-none placeholder:text-stone-400 dark:placeholder:text-stone-500"
            />
            <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200">
              <X size={15} />
            </button>
          </div>

          <Command.List className="max-h-[400px] overflow-y-auto py-2">
            <Command.Empty className="py-6 text-center text-sm text-stone-400">
              No results found.
            </Command.Empty>

            {/* Chanakya — always first */}
            <Command.Group>
              <Command.Item
                value="ask-chanakya"
                onSelect={() => handleSelect('__chanakya__')}
                className="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg cursor-pointer
                  text-sm text-stone-700 dark:text-stone-300
                  data-[selected=true]:bg-orange-50 dark:data-[selected=true]:bg-orange-950/40
                  data-[selected=true]:text-orange-600 dark:data-[selected=true]:text-orange-400"
              >
                <MessageSquare size={15} className="text-orange-500 shrink-0" />
                <span className="font-semibold">Ask Chanakya…</span>
              </Command.Item>
            </Command.Group>

            {visibleGroups.map((group) => (
              <Command.Group key={group.label} heading={group.label}>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Command.Item
                      key={`${item.path}-${item.label}`}
                      value={`${group.label} ${item.label}`}
                      onSelect={() => handleSelect(item.path)}
                      className="flex items-center gap-3 px-4 py-2 mx-2 rounded-lg cursor-pointer
                        text-sm text-stone-600 dark:text-stone-400
                        data-[selected=true]:bg-orange-50 dark:data-[selected=true]:bg-orange-950/40
                        data-[selected=true]:text-orange-600 dark:data-[selected=true]:text-orange-400"
                    >
                      <Icon size={14} className="shrink-0" />
                      {item.label}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}

            {!user && (
              <div className="px-4 py-3 text-xs text-center text-stone-400 border-t border-stone-100 dark:border-stone-800 mt-2">
                <a href="/login" className="text-orange-500 font-semibold hover:underline">Sign in</a>
                {' '}to unlock all pages
              </div>
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
